import React from 'react';
import CloseIcon from 'svg/CloseIcon';
import InfoCircle from 'svg/InfoCircle';
import { InvitePlayerGuideModalProps } from 'screens/CoachLoggedInScreen/props';
import { ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';

const features = [
  {
    title: 'Lesson Recaps & Progress Tracking',
    description:
      'Keep a shared history of sessions, notes, and key takeaways to help players grow faster.',
  },
  {
    title: 'Seamless, Cash-Free Payments',
    description:
      'Say goodbye to chasing payments. Get paid easily and on time, directly through the platform.',
  },
  {
    title: 'Simplified Scheduling',
    description:
      'Players can view your availability and book or you can create lesson and invite your players directly.',
  },
];

const invitationMethods = [
  {
    number: '1.',
    description: 'Invite your players by',
    highlight: 'email',
    rest: ". They'll receive a letter with a registration link, and once they sign up, they'll automatically be added to your account.",
  },
  {
    number: '2.',
    description: 'Share link or QR code to your',
    highlight: 'Listing',
    rest: '. Ask players to',
    highlight2: 'Follow',
    rest2: ' you.',
  },
];

const InvitePlayerGuideModal: React.FC<InvitePlayerGuideModalProps> = ({ isOpen, handleClose }) => {
  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      classNameMaxWidth="sm:max-w-2xl"
      classNameRounded="rounded-3xl"
    >
      <div className="flex flex-col">
        {/* Modal Header */}
        <div className="flex flex-row items-center justify-between border-b border-color-border-card-lightmode px-10 py-5 dark:border-color-border-card-darkmode max-sm:px-ds-lg">
          <div className="flex flex-row items-center gap-4">
            <div className="h-6 w-6">
              <InfoCircle className="h-full w-full text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
            </div>
            <h2 className="typography-product-heading-desktop max-sm:typography-product-heading-mobile">
              Invite your players
            </h2>
          </div>
          <ButtonText onClick={handleClose}>
            <CloseIcon className="h-6 w-6 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-sm:h-3.5 max-sm:w-3.5" />
          </ButtonText>
        </div>

        {/* Modal Content */}
        <div className="flex flex-col items-center px-10 py-4 pb-8 max-sm:px-ds-lg">
          <div className="flex w-full flex-col gap-8">
            {/* Introduction */}
            <div className="flex flex-col gap-3">
              <p className="typography-product-body-highlight  text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Bringing your players onto the platform makes your coaching business smoother,
                smarter, and more professional. When your clients join you here, you both get access
                to powerful features designed to enhance the coaching experience:
              </p>

              {/* Feature List */}
              <div className="flex flex-col gap-2">
                {features.map((feature, index) => (
                  <div key={index} className="flex flex-row items-start gap-3">
                    <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-color-bg-lightmode-invert dark:bg-color-bg-darkmode-invert" />

                    <p className="typography-product-body text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                      <span className="typography-product-body-highlight">{feature.title}</span>
                      <span className="text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                        {' '}
                        – {feature.description}
                      </span>
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Invitation Methods */}
            <div className="flex flex-col gap-3">
              <p className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                You have two ways to invite players:
              </p>

              {invitationMethods.map((method, index) => (
                <div key={index} className="flex flex-row items-start gap-3">
                  <span className="typography-product-body-highlight flex-shrink-0 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                    {method.number}
                  </span>
                  <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    {method.description}{' '}
                    <span className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                      {method.highlight}
                    </span>
                    {method.rest}
                    {method.highlight2 && (
                      <>
                        {' '}
                        <span className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                          {method.highlight2}
                        </span>
                      </>
                    )}
                    {method.rest2}
                  </p>
                </div>
              ))}
            </div>

            {/* Note */}
            <p className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              Players are considered yours once they create a Bounce account and appear in your
              Players list.
            </p>
            <img src="/images/coaches/invite-player-guide.png" alt="Invite Player Guide" />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default InvitePlayerGuideModal;
